import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayoutProviders, SidebarLayout } from '../layout/SidebarLayout';
import { patientsService } from '../adapters/patients';
import { notesService } from '../adapters/notes';
import { usePatients } from '../contexts/PatientsContext';
import toast from '../lib/toast';
import PatientHeader from '../components/patient/PatientHeader';
import { ActionRail } from '../components/patient/ActionRail';
import { NotesTimeline } from '../components/patient/NotesTimeline';
import { Patient, SOAPNoteMetadata } from '../types';

interface EditForm {
  first_name: string;
  last_name: string;
  sex_at_birth: string;
  date_of_birth: string;
  email: string;
  phone: string;
}

const PatientDetailInner = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { recordRecent, deactivatePatient, savePatient } = usePatients();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({
    first_name: '', last_name: '', sex_at_birth: 'unknown',
    date_of_birth: '', email: '', phone: '',
  });
  const [soapNotes, setSoapNotes] = useState<SOAPNoteMetadata[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!id) return;
    setNotesLoading(true);
    setNotesError(null);
    try {
      const res = await notesService.listPatientNotes(id, 50);
      setSoapNotes(res.items || []);
    } catch (e: any) {
      setNotesError(e?.response?.data?.msg || 'Failed to load notes');
    } finally {
      setNotesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await patientsService.getPatient(id);
        if (!mounted) return;
        setPatient(data);
        recordRecent(data);
      } catch (e: any) {
        toast.error(e?.response?.data?.msg || 'Failed to load patient');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [id, recordRecent]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent;
      if (!ce?.detail?.patientId || ce.detail.patientId !== id) return;
      fetchNotes();
    };
    window.addEventListener('therapix:patient-notes-refresh', handler);
    return () => window.removeEventListener('therapix:patient-notes-refresh', handler);
  }, [id, fetchNotes]);

  const openEdit = useCallback(() => {
    if (!patient) return;
    setEditForm({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      sex_at_birth: patient.sex_at_birth || 'unknown',
      date_of_birth: patient.date_of_birth || '',
      email: patient.email || '',
      phone: patient.phone || '',
    });
    setEditOpen(true);
  }, [patient]);

  const closeEdit = useCallback(() => {
    if (editSubmitting) return;
    setEditOpen(false);
  }, [editSubmitting]);

  const onEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditForm(f => ({ ...f, [name]: value }));
  };

  const submitEdit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patient?.patient_id) return;
    if (!editForm.first_name?.trim() || !editForm.last_name?.trim()) {
      toast.error('First and last name required');
      return;
    }
    setEditSubmitting(true);
    try {
      const payload: Partial<Patient> = {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        sex_at_birth: editForm.sex_at_birth,
        date_of_birth: editForm.date_of_birth,
        email: editForm.email,
        phone: editForm.phone,
      };
      (['date_of_birth', 'email', 'phone'] as const).forEach(k => { if (!payload[k]) delete payload[k]; });
      const updated = await savePatient(patient.patient_id, payload);
      setPatient(updated);
      recordRecent(updated);
      toast.success('Patient updated');
      setEditOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.msg || 'Failed to update patient');
    } finally {
      setEditSubmitting(false);
    }
  }, [patient?.patient_id, editForm, savePatient, recordRecent]);

  const confirmDeactivate = async () => {
    if (!patient || !window.confirm('Delete this patient? This cannot be undone.')) return;
    try {
      await deactivatePatient(patient.patient_id);
      toast.success('Patient removed');
      navigate('/patients');
    } catch (e: any) {
      toast.error(e?.response?.data?.msg || 'Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className='px-2 sm:px-4 py-6'>
        <div className='animate-pulse h-20 bg-white rounded-lg border border-brand-navy/15 mb-6' />
        <div className='animate-pulse h-40 bg-white rounded-lg border border-brand-navy/15' />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className='px-4 py-8 text-sm text-red-500'>Patient not found.</div>
    );
  }

  return (
    <>
      {editOpen && (
        <div className='fixed inset-0 z-[100] flex items-center justify-center p-4'>
          <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={closeEdit} />
          <div className='relative bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden'>
            <div className='bg-brand-navy px-6 py-4 flex items-center justify-between'>
              <h2 className='text-xl font-semibold text-white'>Edit Patient</h2>
              <button type='button' onClick={closeEdit} disabled={editSubmitting}
                className='text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 rounded px-2 py-1 disabled:opacity-50'>✕</button>
            </div>
            <form onSubmit={submitEdit} className='p-6'>
              <div className='space-y-5'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1.5'>First Name <span className='text-red-500'>*</span></label>
                    <input name='first_name' value={editForm.first_name} onChange={onEditChange} required disabled={editSubmitting}
                      placeholder='Enter first name'
                      className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm' />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1.5'>Last Name <span className='text-red-500'>*</span></label>
                    <input name='last_name' value={editForm.last_name} onChange={onEditChange} required disabled={editSubmitting}
                      placeholder='Enter last name'
                      className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm' />
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1.5'>Sex at Birth</label>
                    <select name='sex_at_birth' value={editForm.sex_at_birth} onChange={onEditChange} disabled={editSubmitting}
                      className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm'>
                      <option value='unknown'>Unknown</option>
                      <option value='male'>Male</option>
                      <option value='female'>Female</option>
                      <option value='intersex'>Intersex</option>
                    </select>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1.5'>Date of Birth</label>
                    <input type='date' name='date_of_birth' value={editForm.date_of_birth} onChange={onEditChange} disabled={editSubmitting}
                      className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm' />
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1.5'>Email</label>
                    <input type='email' name='email' value={editForm.email} onChange={onEditChange} disabled={editSubmitting}
                      placeholder='email@example.com'
                      className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm' />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1.5'>Phone</label>
                    <input name='phone' value={editForm.phone} onChange={onEditChange} disabled={editSubmitting}
                      placeholder='(123) 456-7890'
                      className='w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-navy/40 focus:border-brand-navy text-sm shadow-sm' />
                  </div>
                </div>
              </div>
              <div className='flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200'>
                <button type='button' onClick={closeEdit} disabled={editSubmitting}
                  className='px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-navy/40 disabled:opacity-50'>Cancel</button>
                <button type='submit' disabled={editSubmitting}
                  className='px-5 py-2.5 text-sm font-medium rounded-lg bg-brand-navy text-white hover:bg-brand-navy/90 focus:outline-none focus:ring-2 focus:ring-brand-navy/40 disabled:opacity-50 shadow-sm'>
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className='px-2 sm:px-4 py-6 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8'>
        <div className='space-y-6 max-w-3xl'>
          <PatientHeader patient={patient} lastNote={soapNotes[0] ?? null} onEdit={openEdit} onDelete={confirmDeactivate} />
          <div className='bg-white shadow-sm border border-brand-navy/15 rounded-lg p-6'>
            <h2 className='text-sm font-semibold text-brand-navy mb-3'>Notes Timeline</h2>
            {notesLoading && <div className='text-xs text-gray-500'>Loading notes…</div>}
            {notesError && !notesLoading && <div className='text-xs text-red-500'>{notesError}</div>}
            {!notesLoading && !notesError && <NotesTimeline patientId={id!} notes={soapNotes} />}
          </div>
        </div>
        <div className='mt-10 lg:mt-0'>
          <div className='sticky top-20 z-20 space-y-4'>
            <ActionRail patient={patient} notes={soapNotes} onRefreshNotes={fetchNotes} />
          </div>
        </div>
      </div>
    </>
  );
};

const PatientDetail = () => (
  <AppLayoutProviders>
    <SidebarLayout>
      <PatientDetailInner />
    </SidebarLayout>
  </AppLayoutProviders>
);

export default PatientDetail;
